import { useMemo } from 'preact/hooks';
import { t } from '../lib/i18n';
import { Sparkles, ArrowRight } from 'lucide-preact';
import { nextCapabilitiesFor, type Capability } from '../lib/next-capabilities';
import type { RecentApp } from '../lib/recent-apps';

interface NextCapabilitiesProps {
  app: RecentApp;
  onRevise: (instruction: string) => void;
}

// §MVP-first / progressive disclosure — the proactive "keep building" surface.
// The lean app is live; Pomela now offers to add ONE capability at a time. The
// PRIMARY suggestion leads (big, with its why); a couple of alternates sit
// beneath as quiet chips. Accepting one re-runs gen in revise mode to graft on
// exactly that piece — so the user keeps building and stays. Deterministic +
// domain-aware (next-capabilities.ts), and it hides capabilities already added
// (matched against this app's version history) so it never re-offers a piece
// that's already there, and disappears entirely once nothing's left to add.
export function NextCapabilities({ app, onRevise }: NextCapabilitiesProps) {
  const caps = useMemo<Capability[]>(() => {
    // Derive what's already been added from the revise history: each revise
    // version's label is the instruction that was run, so a capability whose
    // instruction appears there is already in the app.
    const addedLabels = (app.versions || [])
      .filter((v) => v.kind === 'revise')
      .map((v) => (v.label || '').toLowerCase());
    const allIds = nextCapabilitiesFor(app.prompt || app.manifest?.name || '', [], 99);
    const already = allIds
      .filter((c) => addedLabels.some((l) => l.includes(c.label.toLowerCase().replace(/^add /, '')) || l.includes(c.id)))
      .map((c) => c.id);
    return nextCapabilitiesFor(app.prompt || app.manifest?.name || '', already, 3);
  }, [app.prompt, app.manifest?.name, app.versions]);

  const primary = caps[0];
  if (!primary) return null;
  const rest = caps.slice(1);

  return (
    <div className="surface flex flex-col gap-3 p-4 animate-vs-slide-up" data-next-capabilities>
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg"
          style={{ background: 'color-mix(in srgb, var(--accent, #F4477E) 12%, transparent)', color: 'var(--accent, #F4477E)' }}
        >
          <Sparkles size={15} />
        </span>
        <div className="text-sm font-semibold text-[color:var(--fg)]">{t('nc.header')}</div>
      </div>

      {/* Primary suggestion — the proactive "want to add this next?" offer. */}
      <button
        type="button"
        onClick={() => onRevise(primary.instruction)}
        className="group flex items-center gap-3 rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-4 py-3 text-left transition-colors hover:bg-[color:var(--bg-elevated)]"
        aria-label={`${t(primary.labelKey)}, ${t(primary.whyKey)}`}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-tight text-[color:var(--fg)]">{t(primary.labelKey)}</span>
          <span className="mt-0.5 block text-[12.5px] leading-snug text-[color:var(--fg-muted)]">{t(primary.whyKey)}</span>
        </span>
        <ArrowRight
          size={18}
          className="shrink-0 text-[color:var(--fg-subtle)] transition-transform group-hover:translate-x-0.5 group-hover:text-[color:var(--accent,#F4477E)]"
        />
      </button>

      {rest.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--fg-subtle)]">{t('nc.or')}</span>
          {rest.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onRevise(c.instruction)}
              className="rounded-full border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-3 py-1.5 text-[12.5px] font-medium text-[color:var(--fg-muted)] transition-colors hover:bg-[color:var(--bg-elevated)] hover:text-[color:var(--fg)]"
              aria-label={`${t(c.labelKey)}, ${t(c.whyKey)}`}
            >
              {t(c.labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
